"use client";
import React from "react";
import { Box, Container, Typography, Button, Chip } from "@mui/material";
import Image from "next/image";
import NextLink from "next/link";
import { useTranslation } from "react-i18next";

const OMNI_ORANGE = "#C2501F";

const AiAgentOmniHeroSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  return (
    <Box
      sx={{
        position: "relative",
        overflow: "hidden",
        py: { xs: 6, sm: 8, md: 10, lg: 5 },
        direction: isArabic ? "rtl" : "ltr",
      }}
    >
      {/* Background image */}
      <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <Image
          src="https://getgeek-website-assets.b-cdn.net/OmnibgColor.webp"
          alt="Background"
          fill
          priority
          style={{ objectFit: "cover", objectPosition: "center" }}
        />
      </Box>

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "55% 45%" },
            gap: { xs: 4, md: 4, lg: 6 },
            alignItems: "center",
            gridTemplateAreas: {
              xs: "'text'",
              md: "'text image'",
            },
            mt: "9%",
          }}
        >
          {/* Left - Text */}
          <Box sx={{ gridArea: "text" }}>
            <Chip
              label={t("aiAgentOmniHero.badge")}
              sx={{
                backgroundColor: "#FFFFFF",
                border: "1px solid #F6D5C3",
                mb: { xs: 2, sm: 2.5, md: 3 },
                color: OMNI_ORANGE,
                fontSize: { xs: "11px", sm: "12px", md: "12px" },
                fontWeight: 600,
                px: { xs: 1.2, sm: 1.1 },
                py: { xs: 1.4, sm: 1.7 },
                fontFamily: "var(--font-outfit)",
                borderRadius: "40px",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            />

            {/* Mobile-only small image, below chip, aligned with text */}
            <Box
              sx={{
                display: { xs: "block", md: "none" },
                position: "relative",
                width: { xs: 90, sm: 110 },
                height: { xs: 90, sm: 110 },
                borderRadius: "50%",
                overflow: "hidden",
                boxShadow: "0 12px 24px rgba(234,88,12,0.25)",
                mb: { xs: 3, sm: 3 },
                mt: { xs: 2, sm: 2.5 }
              }}
            >
              <Image
                src="https://getgeek-website-assets.b-cdn.net/OmniRightSideIMage.webp"
                alt="Omni AI recruiting chatbot and platform assistant avatar"
                fill
                style={{ objectFit: "cover" }}
              />
            </Box>

            <Typography
              variant="h1"
              sx={{
                fontSize: {
                  xs: "28px",
                  sm: "34px",
                  md: "42px",
                  lg: "48px",
                  xl: "52px",
                },
                fontWeight: 700,
                lineHeight: 1.25,
                color: "#14122B",
                fontFamily: "var(--font-plusjakarta)",
                mb: { xs: 2, sm: 2.5, md: 3 },
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("aiAgentOmniHero.heading.main")}{" "}
              <Box component="span" sx={{ color: "#C2501F" }}>
                {t("aiAgentOmniHero.heading.highlight")}
              </Box>
            </Typography>

            <Typography
              sx={{
                fontSize: { xs: "14px", sm: "15px", md: "16px", lg: "17px" },
                color: "#55556E",
                lineHeight: 1.7,
                fontWeight: 400,
                fontFamily: "var(--font-outfit)",
                maxWidth: { xs: "100%", md: 480, lg: 690 },
                mb: { xs: 3, sm: 3.5, md: 4 },
                textAlign: isArabic ? "right" : "left",
              }}
            >
              {t("aiAgentOmniHero.description")}
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 2, sm: 2.5 },
                flexWrap: "wrap",
              }}
            >
              <NextLink
                href="/free-trial-of-ai-recruiting-tool"
                passHref
                legacyBehavior
              >
                <Button
                  component="a"
                  sx={{
                    bgcolor: "#000",
                    color: "white",
                    px: { xs: 3, sm: 3.5 },
                    py: { xs: 1.2, sm: 1.4 },
                    borderRadius: "50px",
                    textTransform: "none",
                    fontSize: { xs: "14px", sm: "15px" },
                    fontWeight: 500,
                    fontFamily: "var(--font-outfit)",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      bgcolor: "#C2501F",
                      transform: "scale(1.03)",
                    },
                  }}
                >
                  {t("aiAgentOmniHero.cta.label")}
                </Button>
              </NextLink>

              <Typography
                sx={{
                  fontSize: { xs: "12px", sm: "13px" },
                  color: "#55556E",
                  fontFamily: "var(--font-outfit)",
                  fontWeight: 400,
                }}
              >
                {t("aiAgentOmniHero.trustNote")}
              </Typography>
            </Box>
          </Box>

          {/* Right - Circular Image (tablet/desktop only) */}
          <Box
            sx={{
              gridArea: "image",
              display: { xs: "none", md: "flex" },
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              sx={{
                position: "relative",
                width: { md: 340, lg: 400 },
                height: { md: 340, lg: 400 },
                borderRadius: "50%",
                // border: "8px solid #FFFFFF",
                boxShadow: "0 40px 70px rgba(234,88,12,0.25)",
                overflow: "hidden",
              }}
            >
              <Image
                src="https://getgeek-website-assets.b-cdn.net/OmniRightSideIMage.webp"
                alt="Omni AI recruiting chatbot and platform assistant avatar"
                fill
                priority
                style={{ objectFit: "cover" }}
              />
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default AiAgentOmniHeroSection;